import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

// Import types and functions explicitly
import type { I18nConfig, SupportedLanguage } from '../i18n/config';
import { 
  defaultI18nConfig, 
  loadI18nConfig, 
  saveI18nConfig,
  formatDate,
  formatTime,
  formatCurrency,
  formatNumber,
} from '../i18n/config';
import { localePresets } from '../i18n/localePresets';
import { translations, type TranslationKey } from '../i18n/translations';

function resolveBcp47(language: SupportedLanguage): string {
  if (language === 'en') return 'en-US';
  if (language === 'es') return 'es-ES';
  return language;
}

interface I18nContextType {
  config: I18nConfig;
  updateConfig: (newConfig: Partial<I18nConfig>) => void;
  formatDate: (date: Date) => string;
  formatTime: (date: Date) => string;
  formatCurrency: (amount: number) => string;
  formatNumber: (number: number) => string;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const fallbackI18nContext: I18nContextType = {
  config: defaultI18nConfig,
  updateConfig: () => undefined,
  formatDate: (date: Date) => formatDate(date, defaultI18nConfig.dateFormat),
  formatTime: (date: Date) => formatTime(date, defaultI18nConfig.timeFormat),
  formatCurrency: (amount: number) => formatCurrency(amount, defaultI18nConfig.currency, defaultI18nConfig.numberFormat),
  formatNumber: (value: number) => formatNumber(value, defaultI18nConfig.numberFormat),
  t: (key: TranslationKey, params?: Record<string, string | number>) => {
    const base = translations[defaultI18nConfig.language as SupportedLanguage]?.[key] || translations.en[key] || key;
    if (!params) return base;
    return Object.keys(params).reduce(
      (acc, paramKey) => acc.replace(new RegExp(`{${paramKey}}`, 'g'), String(params[paramKey])),
      base,
    );
  },
};

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<I18nConfig>(loadI18nConfig());

  useEffect(() => {
    saveI18nConfig(config);
    document.documentElement.lang = config.language;
  }, [config]);

  const updateConfig = (newConfig: Partial<I18nConfig>) => {
    setConfig((prev) => {
      const next = { ...prev, ...newConfig };
      if (newConfig.language && newConfig.language !== prev.language) {
        const preset = localePresets[newConfig.language];
        if (preset) {
          return { ...next, ...preset, language: newConfig.language };
        }
      }
      return next;
    });
  };

  const bcp47 = resolveBcp47(config.language);

  const formatDateWithConfig = (date: Date): string => {
    return formatDate(date, config.dateFormat, bcp47);
  };

  const formatTimeWithConfig = (date: Date): string => {
    return formatTime(date, config.timeFormat, bcp47);
  };

  const formatCurrencyWithConfig = (amount: number): string => {
    return formatCurrency(amount, config.currency, config.numberFormat);
  };

  const formatNumberWithConfig = (number: number): string => {
    return formatNumber(number, config.numberFormat);
  };

  // Translation function with real translations and interpolation support
  const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
    const language = config.language as SupportedLanguage;
    let translation = translations[language]?.[key] || translations.en[key] || key;
    
    // Replace placeholders with actual values if params are provided
    if (params) {
      Object.keys(params).forEach(paramKey => {
        const placeholder = `{${paramKey}}`;
        translation = translation.replace(new RegExp(placeholder, 'g'), String(params[paramKey]));
      });
    }
    
    return translation;
  };

  return (
    <I18nContext.Provider
      value={{
        config,
        updateConfig,
        formatDate: formatDateWithConfig,
        formatTime: formatTimeWithConfig,
        formatCurrency: formatCurrencyWithConfig,
        formatNumber: formatNumberWithConfig,
        t
      }}
    >
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    const mode = (import.meta as any)?.env?.MODE;
    if (mode !== 'test' && mode !== 'production') {
      console.warn('useI18n called outside I18nProvider. Falling back to default configuration.');
    }
    return fallbackI18nContext;
  }
  return context;
};